import dayjs from 'dayjs';
import { Type } from 'class-transformer';
import { Model } from './model';
import { TransformStringToDayJS } from './utils';
import { BankTransactionBase } from './bank_transaction';
import type { AgentParsingErrorCode } from './types';

export enum BankStatementStatus {
  SUBMITTED = 'submitted',
  PROCESSING = 'processing',
  INVALID = 'invalid',
  PENDING_APPROVAL = 'pending_approval',
  APPROVED = 'approved',
  SPLIT = 'split',
  SYNCED = 'synced',
}

/**
 * Processing error details.
 */
export interface ProcessingError {
  error_code: string;
  error_message: string;
}

/**
 * Attachment information.
 */
export interface BankStatementAttachment {
  id: string;
  name: string;
  src?: string;
  mime_type?: string;
  file_type?: string;
}

/**
 * Basic info about a child statement from a split.
 */
export interface ChildStatementInfo {
  id: string;
  name: string;
}

/**
 * Split statement information (detail view only).
 */
export interface SplitStatementInfo {
  is_split_parent: boolean;
  split_count?: number;
  child_statements: ChildStatementInfo[];
  original_statement_id?: string;
  split_index?: number;
}

/**
 * Transaction within a bank statement.
 */
export interface BankStatementTransaction {
  id: string;
  account_id?: string;
  timestamp: string;
  amount: number;
  currency?: string;
  type: string;
  description?: string;
  reference_number?: string;
  position?: number;
}

/**
 * Bank statement list item - returned by list endpoint.
 */
export class BankStatementListItem extends Model {
  id!: string;
  status!: BankStatementStatus;
  account_id?: string;
  account_number?: string;
  attachment!: BankStatementAttachment;
  processing_error?: ProcessingError;
  transaction_count!: number;
  total_deposits?: number;
  total_withdrawals?: number;
  account_mask?: string;
  account_name?: string;
  currency?: string;

  @TransformStringToDayJS()
  period_start?: dayjs.Dayjs;

  @TransformStringToDayJS()
  period_end?: dayjs.Dayjs;

  /**
   * Get the attachment name.
   */
  get attachment_name(): string {
    return this.attachment.name;
  }

  /**
   * Alias for transaction_count (backwards compatibility).
   */
  get transactions_count(): number {
    return this.transaction_count;
  }

  /**
   * Whether this statement is ready for user action (not processing).
   */
  get isReady(): boolean {
    return (
      this.status !== BankStatementStatus.SUBMITTED &&
      this.status !== BankStatementStatus.PROCESSING
    );
  }

  /**
   * Whether this statement failed processing.
   */
  get isInvalid(): boolean {
    return this.status === BankStatementStatus.INVALID;
  }

  /**
   * Whether this is a parent statement that was split into children.
   */
  get isSplitParent(): boolean {
    return this.status === BankStatementStatus.SPLIT;
  }
}

/**
 * Bank statement detail - returned by get-by-id endpoint.
 * Includes split_info and transactions.
 */
export class BankStatement extends BankStatementListItem {
  split_info!: SplitStatementInfo;

  @Type(() => BankTransactionBase)
  transactions: BankTransactionBase[] = [];

  /**
   * Get the human-readable error message.
   */
  get agent_error_message(): string | undefined {
    return this.processing_error?.error_message;
  }
}
